/* eslint-disable @typescript-eslint/no-unused-vars */
import { Container } from '@untitled-ui/icons-react/build/cjs';
import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NotFoundUI from 'src/components/NotFoundUI';

// Three.js types for CDN usage
declare global {
    interface Window {
        THREE: any;
        OrbitControls: any;
        EffectComposer: any;
        RenderPass: any;
        ShaderPass: any;
        FXAAShader: any;
    }
}

interface IProps {
    height: number;
    width: number;
    depth: number;
}

const ThreeJsScene = ({ height = 0, width = 0, depth = 0 }: IProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isSpinning, setIsSpinning] = useState(false);
    const controls = useRef<any>(null);
    const [nonEmptyFieldCount, setNonEmptyFieldCount] = useState(0);
    const { t } = useTranslation();
    const [threeLoaded, setThreeLoaded] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Load Three.js from CDN using import map
    useEffect(() => {
        const loadThreeJS = async () => {
            try {
                // Check if THREE is already loaded
                if (window.THREE) {
                    setThreeLoaded(true);

                    return;
                }

                // Add import map to head if not already present
                let importMap = document.querySelector('script[type="importmap"]') as HTMLScriptElement;

                if (!importMap) {
                    importMap = document.createElement('script');
                    importMap.type = 'importmap';
                    importMap.textContent = JSON.stringify({
                        imports: {
                            three: 'https://cdn.jsdelivr.net/npm/three@0.178.0/build/three.module.js',
                            'three/addons/': 'https://cdn.jsdelivr.net/npm/three@0.178.0/examples/jsm/',
                        },
                    });
                    document.head.appendChild(importMap);

                    // Wait for the import map to be processed
                    await new Promise((resolve) => setTimeout(resolve, 1000));
                }

                // Load Three.js using dynamic import - bypass webpack resolution
                // @ts-ignore
                const THREE = await eval('import("three")');
                // Make THREE available globally
                window.THREE = THREE;

                // Try to load OrbitControls separately
                try {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore - OrbitControls import for CDN
                    const { OrbitControls } = await eval('import("three/addons/controls/OrbitControls.js")');
                    window.OrbitControls = OrbitControls;
                    const { EffectComposer } = await eval('import("three/addons/postprocessing/EffectComposer.js")');
                    window.EffectComposer = EffectComposer;
                    const { RenderPass } = await eval('import("three/addons/postprocessing/RenderPass.js")');
                    window.RenderPass = RenderPass;
                    const { ShaderPass } = await eval('import("three/addons/postprocessing/ShaderPass.js")');
                    window.ShaderPass = ShaderPass;
                    const { FXAAShader } = await eval('import("three/addons/shaders/FXAAShader.js")');
                    window.FXAAShader = FXAAShader;
                } catch (subModuleError) {
                    console.log('Submodule error', subModuleError);
                }

                setTimeout(() => {
                    setThreeLoaded(true);
                }, 1000);
            } catch (err) {
                console.error('Failed to load Three.js:', err);
                setError('Failed to load Three.js');
            }
        };

        loadThreeJS();
    }, []);

    useEffect(() => {
        setNonEmptyFieldCount([height, width, depth].filter((val) => val !== undefined && val !== 0 && !isNaN(val)).length);
    }, [height, width, depth]);

    useEffect(() => {
        if (!threeLoaded || !canvasRef.current || nonEmptyFieldCount <= 1) {
            return;
        }

        const THREE = window.THREE;

        if (!THREE) {
            setError('Three.js not available');

            return;
        }

        try {
            const scene = new THREE.Scene();
            const camera = new THREE.PerspectiveCamera(100, 395 / 454, 0.8, 1000);
            const renderer = new THREE.WebGLRenderer({
                canvas: canvasRef.current,
                antialias: true,
                logarithmicDepthBuffer: true,
            });

            renderer.setSize(454, 395);
            renderer.setClearColor(0xffffff);
            renderer.setPixelRatio(window.devicePixelRatio);

            const maxDimension = Math.max(width, height, depth);
            const fov = camera.fov * (Math.PI / 180); // Convert FOV to radians
            const cameraDistance = Math.max((maxDimension / (2 * Math.tan(fov / 2))) * 1.5, 8);

            camera.position.set(cameraDistance * 0.7, cameraDistance * 0.7, cameraDistance);
            camera.lookAt(0, 0, 0);

            const geometry = new THREE.BoxGeometry(width, height, depth);
            const material = new THREE.MeshBasicMaterial({
                color: 0x444ce7,
                opacity: 0.65,
                transparent: true,
            });
            const cube = new THREE.Mesh(geometry, material);
            scene.add(cube);

            const edges = new THREE.EdgesGeometry(geometry);
            const lineMaterial = new THREE.LineBasicMaterial({
                color: 0x000000,
                linewidth: 1,
            });
            const wireframe = new THREE.LineSegments(edges, lineMaterial);
            wireframe.computeLineDistances();
            cube.add(wireframe);

            controls.current = new window.OrbitControls(camera, renderer.domElement);
            controls.current.enableDamping = true;

            // Set the target to the center of the object
            controls.current.target.set(0, 0, 0);
            controls.current.update();

            const composer = new window.EffectComposer(renderer);
            const renderPass = new window.RenderPass(scene, camera);
            composer.addPass(renderPass);

            const fxaaPass = new window.ShaderPass(window.FXAAShader);
            fxaaPass.material.precision = 'highp';
            // eslint-disable-next-line @typescript-eslint/dot-notation
            fxaaPass.uniforms.resolution.value.set(1 / window.innerWidth, 1 / window.innerHeight);
            composer.addPass(fxaaPass);

            const animate = () => {
                requestAnimationFrame(animate);

                if (isSpinning) {
                    cube.rotation.x += 0.01;
                    cube.rotation.y += 0.01;
                }

                if (controls.current) {
                    controls.current.update();
                }

                composer.render(); // Use composer instead of renderer
            };

            animate();

            return () => {
                geometry.dispose();
                material.dispose();
                lineMaterial.dispose();
                renderer.dispose();
            };
        } catch (err: any) {
            console.log('Failed to create 3D scene', err);
            setError('Failed to create 3D scene');
        }
    }, [isSpinning, height, width, depth, threeLoaded, nonEmptyFieldCount]);

    const toggleSpin = () => {
        setIsSpinning(!isSpinning);
    };

    if (error) {
        return (
            <div className="md:h-full w-full flex flex-col gap-2 justify-center items-center border border-red-500">
                <div className="text-red-500 text-center p-4">
                    <p>
                        {t('QuotingHub.QuotingDashboard.Error_loading_3D_preview')}: {error}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="md:h-full w-full flex flex-col gap-2 justify-center items-center">
            <canvas ref={canvasRef} className={`max-w-full !w-auto !h-auto ${nonEmptyFieldCount > 1 ? '' : 'hidden'}`} />
            <button className="hidden" onClick={toggleSpin}>
                {isSpinning ? t('QuotingHub.QuotingDashboard.Stop_Spin') : t('QuotingHub.QuotingDashboard.Start_Spin')}
            </button>

            {nonEmptyFieldCount <= 1 && (
                <NotFoundUI
                    title={t('QuotingHub.QuotingDashboard.Unit_Preview_Not_Available')}
                    iconClassName="hidden"
                    iconSecondaryClassName="!flex"
                    iconSecondary={<Container className="w-5 h-5 text-primary" />}
                    desc={t('QuotingHub.QuotingDashboard.Unit_Preview_Not_Available_desc')}
                    containerClassName="min-h-[250px] !h-auto"
                />
            )}
        </div>
    );
};

export default ThreeJsScene;
